import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Image as ImageIcon, Upload, Search, Trash2, ZoomIn, X } from 'lucide-react';
import { defaultsAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const Defaults = () => {
  const { canAdd, canEdit, canDelete } = useAuth();
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  
  const [uploadData, setUploadData] = useState({
    filename: '',
    file: null,
    preview: null
  });

  // Load images
  const loadImages = async () => {
    try {
      setIsLoading(true);
      const data = await defaultsAPI.getAll();
      setImages(data);
      setFilteredImages(data);
    } catch (error) {
      toast.error('Failed to load images');
      console.error('Error loading images:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadImages();
  }, []);

  // Search filter
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredImages(images);
    } else {
      const filtered = images.filter(img =>
        img.filename.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredImages(filtered);
    }
  }, [searchQuery, images]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadData({
          ...uploadData,
          file: file,
          preview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload image
  const handleUpload = async () => {
    if (!uploadData.filename || !uploadData.file) {
      toast.error('Please provide filename and select an image');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('filename', uploadData.filename);

      const uploaded = await defaultsAPI.upload(formData);
      setImages([uploaded, ...images]);
      toast.success('Image uploaded successfully');
      setIsUploadDialogOpen(false);
      setUploadData({ filename: '', file: null, preview: null });
    } catch (error) {
      toast.error('Failed to upload image');
      console.error('Error uploading:', error);
    }
  };

  // Delete image
  const handleDelete = async (id, filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) return;

    try {
      await defaultsAPI.delete(id);
      setImages(images.filter(img => img.id !== id));
      toast.success('Image deleted');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  // Open zoom view
  const handleZoom = (image) => {
    setSelectedImage(image);
    setIsZoomOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-primary/10">
            <ImageIcon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Defaults</h1>
            <p className="text-muted-foreground mt-1">Image library and reference manager</p>
          </div>
        </div>
        {canAdd('defaults') && (
          <Button onClick={() => setIsUploadDialogOpen(true)} className="shadow-emerald">
            <Upload className="h-4 w-4 mr-2" />
            Upload Image
          </Button>
        )}
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by filename..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Images Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Images ({filteredImages.length})</CardTitle>
          <CardDescription>
            {searchQuery ? `Showing results for "${searchQuery}"` : 'All uploaded images'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading images...</p>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchQuery ? 'No images found matching your search' : 'No images uploaded yet'}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {!searchQuery && 'Click "Upload Image" to add your first image'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {filteredImages.map((image) => (
                <div
                  key={image.id}
                  className="group relative border rounded-lg overflow-hidden hover:shadow-lg transition-shadow bg-muted/30"
                >
                  {/* Image Tile */}
                  <div
                    className="aspect-square cursor-pointer relative"
                    onClick={() => handleZoom(image)}
                  >
                    <img
                      src={`data:${image.content_type};base64,${image.image_data}`}
                      alt={image.filename}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  
                  {/* Filename */}
                  <div className="p-2 bg-background">
                    <p className="text-xs font-medium truncate" title={image.filename}>
                      {image.filename}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(image.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Delete Button */}
                  {canDelete('defaults') && (
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute top-2 right-2 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(image.id, image.filename);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Image</DialogTitle>
            <DialogDescription>Add a new image to your defaults library</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Filename *</Label>
              <Input
                type="text"
                placeholder="Enter filename (e.g., design-001)"
                value={uploadData.filename}
                onChange={(e) => setUploadData({ ...uploadData, filename: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Select Image *</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
              />
            </div>

            {uploadData.preview && (
              <div className="border rounded-lg p-4">
                <p className="text-sm font-medium mb-2">Preview:</p>
                <img
                  src={uploadData.preview}
                  alt="Preview"
                  className="w-full h-48 object-contain rounded"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsUploadDialogOpen(false);
                setUploadData({ filename: '', file: null, preview: null });
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpload}>Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zoom Dialog */}
      <Dialog open={isZoomOpen} onOpenChange={setIsZoomOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{selectedImage?.filename}</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsZoomOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="py-4">
              <img
                src={`data:${selectedImage.content_type};base64,${selectedImage.image_data}`}
                alt={selectedImage.filename}
                className="w-full h-auto max-h-[70vh] object-contain rounded"
              />
              <div className="mt-4 text-sm text-muted-foreground">
                <p>Uploaded: {new Date(selectedImage.created_at).toLocaleString()}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Defaults;
